import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import './WheelOfChores.css';

type Frequency = 'Daily' | 'Weekly' | 'Monthly';
type Tab = 'wheel' | 'chores' | 'people' | 'rewards';
type ExperienceMode = 'web' | 'phone';
type PersonFilter = 'all' | string;

type WheelOfChoresProps = {
  initialExperienceMode?: ExperienceMode;
  showModeSwitch?: boolean;
};

type Chore = {
  id: string;
  name: string;
  frequency: Frequency;
  minutes: number;
  assigneeIds: string[];
};

type Completion = {
  id: string;
  choreId: string;
  choreName: string;
  personId: string;
  personName: string;
  frequency: Frequency;
  minutes: number;
  coins: number;
  completedAt: string;
  periodKey: string;
};

type Reward = {
  id: string;
  name: string;
  cost: number;
};

type Person = {
  id: string;
  name: string;
  coins: number;
};

type WheelStore = {
  people: Person[];
  chores: Chore[];
  rewards: Reward[];
  history: Completion[];
  coins?: number;
  maxDailyMinutes: number;
  todayWheel: {
    date: string;
    personId: PersonFilter;
    choreIds: string[];
  };
};

const storageKey = 'wheel-of-chores';
const frequencies: Frequency[] = ['Daily', 'Weekly', 'Monthly'];
const segmentColors = ['#ff5c7a', '#ffd166', '#7dd3fc', '#a78bfa', '#fb7185', '#f97316'];

const defaultStore: WheelStore = {
  people: [
    { id: 'seed-alex', name: 'Alex', coins: 0 },
    { id: 'seed-sam', name: 'Sam', coins: 0 },
  ],
  chores: [
    { id: 'seed-dishes', name: 'Reset the dishes', frequency: 'Daily', minutes: 12, assigneeIds: ['seed-alex', 'seed-sam'] },
    { id: 'seed-laundry', name: 'Fold one laundry basket', frequency: 'Weekly', minutes: 25, assigneeIds: ['seed-alex'] },
    { id: 'seed-fridge', name: 'Wipe fridge shelves', frequency: 'Monthly', minutes: 20, assigneeIds: ['seed-sam'] },
  ],
  rewards: [
    { id: 'seed-coffee', name: 'Fancy coffee run', cost: 35 },
    { id: 'seed-game', name: 'Play video games for 1 hour', cost: 60 },
  ],
  history: [],
  maxDailyMinutes: 45,
  todayWheel: {
    date: '',
    personId: 'all',
    choreIds: [],
  },
};

function createId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function getDateKey(date = new Date()) {
  return date.toISOString().slice(0, 10);
}

function getWeekKey(date = new Date()) {
  const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = utcDate.getUTCDay() || 7;
  utcDate.setUTCDate(utcDate.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((utcDate.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${utcDate.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}

function getPeriodKey(frequency: Frequency, date = new Date()) {
  if (frequency === 'Daily') {
    return getDateKey(date);
  }

  if (frequency === 'Weekly') {
    return getWeekKey(date);
  }

  return getDateKey(date).slice(0, 7);
}

function getMultiplier(frequency: Frequency) {
  if (frequency === 'Weekly') {
    return 1.5;
  }

  if (frequency === 'Monthly') {
    return 2.5;
  }

  return 1;
}

function getCoinValue(chore: Chore) {
  return Math.round(chore.minutes * getMultiplier(chore.frequency));
}

function normalizePeople(parsedStore: Partial<WheelStore>) {
  const parsedPeople = Array.isArray(parsedStore.people) ? parsedStore.people : [];
  const people = parsedPeople
    .filter((person): person is Person => Boolean(person?.id && person?.name))
    .map((person) => ({
      id: person.id,
      name: person.name,
      coins: Number.isFinite(person.coins) ? person.coins : 0,
    }));

  if (people.length > 0) {
    return people;
  }

  return defaultStore.people.map((person, index) => ({
    ...person,
    coins: index === 0 && Number.isFinite(parsedStore.coins) ? Number(parsedStore.coins) : person.coins,
  }));
}

function normalizeChores(parsedStore: Partial<WheelStore>, people: Person[]) {
  const fallbackAssigneeIds = people.map((person) => person.id);
  const parsedChores = Array.isArray(parsedStore.chores) ? parsedStore.chores : defaultStore.chores;

  return parsedChores
    .filter((chore): chore is Chore => Boolean(chore?.id && chore?.name))
    .map((chore) => {
      const assigneeIds = Array.isArray(chore.assigneeIds)
        ? chore.assigneeIds.filter((id) => people.some((person) => person.id === id))
        : fallbackAssigneeIds;

      return {
        ...chore,
        assigneeIds: assigneeIds.length > 0 ? assigneeIds : fallbackAssigneeIds,
      };
    });
}

function getAssigneeNames(chore: Chore, people: Person[]) {
  return chore.assigneeIds
    .map((id) => people.find((person) => person.id === id)?.name)
    .filter((name): name is string => Boolean(name))
    .join(', ');
}

function choreMatchesPerson(chore: Chore, personId: PersonFilter) {
  return personId === 'all' || chore.assigneeIds.includes(personId);
}

function loadStore(): WheelStore {
  if (typeof window === 'undefined') {
    return defaultStore;
  }

  try {
    const rawStore = window.localStorage.getItem(storageKey);
    if (!rawStore) {
      return defaultStore;
    }

    const parsedStore = JSON.parse(rawStore) as Partial<WheelStore>;
    const people = normalizePeople(parsedStore);
    const chores = normalizeChores(parsedStore, people);

    return {
      ...defaultStore,
      ...parsedStore,
      people,
      chores,
      rewards: Array.isArray(parsedStore.rewards) ? parsedStore.rewards : defaultStore.rewards,
      history: Array.isArray(parsedStore.history) ? parsedStore.history : defaultStore.history,
      todayWheel: {
        ...defaultStore.todayWheel,
        ...parsedStore.todayWheel,
        personId: parsedStore.todayWheel?.personId ?? 'all',
      },
    };
  } catch {
    return defaultStore;
  }
}

function shuffleChores(chores: Chore[]) {
  return [...chores].sort(() => Math.random() - 0.5);
}

function buildWheelGradient(count: number) {
  if (count === 0) {
    return 'radial-gradient(circle, rgba(255, 92, 122, 0.24), rgba(9, 18, 32, 0.96) 62%)';
  }

  const step = 360 / count;
  return `conic-gradient(${Array.from({ length: count }, (_, index) => {
    const start = index * step;
    const end = (index + 1) * step;
    return `${segmentColors[index % segmentColors.length]} ${start}deg ${end}deg`;
  }).join(', ')})`;
}

function WheelOfChores({ initialExperienceMode = 'web', showModeSwitch = true }: WheelOfChoresProps) {
  const [store, setStore] = useState<WheelStore>(loadStore);
  const [activeTab, setActiveTab] = useState<Tab>('wheel');
  const [experienceMode, setExperienceMode] = useState<ExperienceMode>(initialExperienceMode);
  const [activePersonId, setActivePersonId] = useState<PersonFilter>('all');
  const [personName, setPersonName] = useState('');
  const [choreName, setChoreName] = useState('');
  const [choreFrequency, setChoreFrequency] = useState<Frequency>('Daily');
  const [choreMinutes, setChoreMinutes] = useState(15);
  const [choreAssigneeIds, setChoreAssigneeIds] = useState<string[]>(() => defaultStore.people.map((person) => person.id));
  const [rewardName, setRewardName] = useState('');
  const [rewardCost, setRewardCost] = useState(50);
  const [rotation, setRotation] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const [selectedChoreId, setSelectedChoreId] = useState<string | null>(null);
  const [completionPersonId, setCompletionPersonId] = useState('');
  const [notice, setNotice] = useState('Generate a wheel that fits today, then spin.');

  const todayKey = getDateKey();
  const todayIds = useMemo(() => {
    const isCurrentWheel = store.todayWheel.date === todayKey && store.todayWheel.personId === activePersonId;
    return isCurrentWheel ? store.todayWheel.choreIds : [];
  }, [activePersonId, store.todayWheel.choreIds, store.todayWheel.date, store.todayWheel.personId, todayKey]);

  const completedKeys = useMemo(() => {
    return new Set(store.history.map((item) => `${item.choreId}:${item.periodKey}`));
  }, [store.history]);

  const availableChores = useMemo(() => {
    return store.chores.filter((chore) => {
      return choreMatchesPerson(chore, activePersonId) && !completedKeys.has(`${chore.id}:${getPeriodKey(chore.frequency)}`);
    });
  }, [activePersonId, completedKeys, store.chores]);

  const todayChores = useMemo(() => {
    return todayIds
      .map((id) => store.chores.find((chore) => chore.id === id))
      .filter((chore): chore is Chore => Boolean(chore))
      .filter((chore) => !completedKeys.has(`${chore.id}:${getPeriodKey(chore.frequency)}`));
  }, [completedKeys, store.chores, todayIds]);

  const selectedChore = selectedChoreId
    ? todayChores.find((chore) => chore.id === selectedChoreId) ?? null
    : null;
  const activePerson = activePersonId === 'all' ? null : store.people.find((person) => person.id === activePersonId) ?? null;
  const completionPeople = selectedChore
    ? store.people.filter((person) => selectedChore.assigneeIds.includes(person.id))
    : [];
  const householdCoins = store.people.reduce((total, person) => total + person.coins, 0);

  const todayMinutes = todayChores.reduce((total, chore) => total + chore.minutes, 0);
  const wheelGradient = buildWheelGradient(todayChores.length);
  const pools = frequencies.map((frequency) => ({
    frequency,
    total: store.chores.filter((chore) => chore.frequency === frequency && choreMatchesPerson(chore, activePersonId)).length,
    available: availableChores.filter((chore) => chore.frequency === frequency).length,
  }));

  useEffect(() => {
    window.localStorage.setItem(storageKey, JSON.stringify(store));
  }, [store]);

  useEffect(() => {
    if (store.people.length > 0 && choreAssigneeIds.length === 0) {
      setChoreAssigneeIds(store.people.map((person) => person.id));
    }
  }, [choreAssigneeIds.length, store.people]);

  useEffect(() => {
    if (activePersonId !== 'all' && !store.people.some((person) => person.id === activePersonId)) {
      setActivePersonId('all');
    }
  }, [activePersonId, store.people]);

  useEffect(() => {
    if (store.todayWheel.date !== todayKey && store.todayWheel.choreIds.length > 0) {
      setStore((currentStore) => ({
        ...currentStore,
        todayWheel: { date: todayKey, personId: activePersonId, choreIds: [] },
      }));
    }
  }, [activePersonId, store.todayWheel.choreIds.length, store.todayWheel.date, todayKey]);

  function addPerson(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedName = personName.trim();
    if (!trimmedName) {
      return;
    }

    const nextPerson = { id: createId('person'), name: trimmedName, coins: 0 };

    setStore((currentStore) => ({
      ...currentStore,
      people: [nextPerson, ...currentStore.people],
    }));
    setChoreAssigneeIds((currentIds) => [nextPerson.id, ...currentIds]);
    setPersonName('');
  }

  function deletePerson(personId: string) {
    if (store.people.length <= 1) {
      setNotice('Keep at least one person in the household.');
      return;
    }

    const fallbackPersonId = store.people.find((person) => person.id !== personId)?.id;
    if (!fallbackPersonId) {
      return;
    }

    setStore((currentStore) => ({
      ...currentStore,
      people: currentStore.people.filter((person) => person.id !== personId),
      chores: currentStore.chores.map((chore) => {
        const nextAssignees = chore.assigneeIds.filter((id) => id !== personId);
        return {
          ...chore,
          assigneeIds: nextAssignees.length > 0 ? nextAssignees : [fallbackPersonId],
        };
      }),
      todayWheel: {
        ...currentStore.todayWheel,
        choreIds: currentStore.todayWheel.choreIds.filter((choreId) => {
          const chore = currentStore.chores.find((item) => item.id === choreId);
          return chore ? !chore.assigneeIds.includes(personId) : true;
        }),
      },
    }));
    setChoreAssigneeIds((currentIds) => {
      const nextIds = currentIds.filter((id) => id !== personId);
      return nextIds.length > 0 ? nextIds : [fallbackPersonId];
    });
    if (activePersonId === personId) {
      setActivePersonId('all');
    }
  }

  function toggleChoreAssignee(personId: string) {
    setChoreAssigneeIds((currentIds) => {
      if (currentIds.includes(personId)) {
        return currentIds.filter((id) => id !== personId);
      }

      return [...currentIds, personId];
    });
  }

  function generateWheel() {
    const selected: Chore[] = [];

    shuffleChores(availableChores).forEach((chore) => {
      const nextTotal = selected.reduce((total, item) => total + item.minutes, 0) + chore.minutes;
      if (nextTotal <= store.maxDailyMinutes) {
        selected.push(chore);
      }
    });

    setStore((currentStore) => ({
      ...currentStore,
      todayWheel: {
        date: todayKey,
        personId: activePersonId,
        choreIds: selected.map((chore) => chore.id),
      },
    }));
    setSelectedChoreId(null);
    setNotice(
      selected.length > 0
        ? `Today's ${activePerson?.name ?? 'household'} wheel has ${selected.length} chores and ${selected.reduce((total, chore) => total + chore.minutes, 0)} planned minutes.`
        : 'No available chores fit inside the current time budget.'
    );
  }

  function spinWheel() {
    if (isSpinning || todayChores.length === 0) {
      return;
    }

    const selectedIndex = Math.floor(Math.random() * todayChores.length);
    const segmentSize = 360 / todayChores.length;
    const targetAngle = 360 - (selectedIndex * segmentSize + segmentSize / 2);
    const nextRotation = rotation + 1440 + targetAngle;

    setIsSpinning(true);
    setSelectedChoreId(null);
    setNotice('Wheel is choosing the next right-sized task...');
    setRotation(nextRotation);

    window.setTimeout(() => {
      setSelectedChoreId(todayChores[selectedIndex].id);
      setCompletionPersonId(
        activePerson?.id && todayChores[selectedIndex].assigneeIds.includes(activePerson.id)
          ? activePerson.id
          : todayChores[selectedIndex].assigneeIds[0]
      );
      setIsSpinning(false);
      setNotice(`${todayChores[selectedIndex].name} is up next.`);
    }, 3600);
  }

  function addChore(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedName = choreName.trim();
    if (!trimmedName || choreMinutes < 1 || choreAssigneeIds.length === 0) {
      return;
    }

    const nextChore: Chore = {
      id: createId('chore'),
      name: trimmedName,
      frequency: choreFrequency,
      minutes: Math.max(1, Math.round(choreMinutes)),
      assigneeIds: choreAssigneeIds,
    };

    setStore((currentStore) => ({
      ...currentStore,
      chores: [nextChore, ...currentStore.chores],
    }));
    setChoreName('');
    setChoreMinutes(15);
  }

  function deleteChore(choreId: string) {
    setStore((currentStore) => ({
      ...currentStore,
      chores: currentStore.chores.filter((chore) => chore.id !== choreId),
      todayWheel: {
        ...currentStore.todayWheel,
        choreIds: currentStore.todayWheel.choreIds.filter((id) => id !== choreId),
      },
    }));
  }

  function completeSelectedChore() {
    if (!selectedChore) {
      return;
    }

    const completionPerson = store.people.find((person) => person.id === completionPersonId) ?? completionPeople[0];
    if (!completionPerson) {
      return;
    }

    const coins = getCoinValue(selectedChore);
    const completion: Completion = {
      id: createId('done'),
      choreId: selectedChore.id,
      choreName: selectedChore.name,
      personId: completionPerson.id,
      personName: completionPerson.name,
      frequency: selectedChore.frequency,
      minutes: selectedChore.minutes,
      coins,
      completedAt: new Date().toISOString(),
      periodKey: getPeriodKey(selectedChore.frequency),
    };

    setStore((currentStore) => ({
      ...currentStore,
      people: currentStore.people.map((person) =>
        person.id === completionPerson.id ? { ...person, coins: person.coins + coins } : person
      ),
      history: [completion, ...currentStore.history].slice(0, 80),
      todayWheel: {
        ...currentStore.todayWheel,
        choreIds: currentStore.todayWheel.choreIds.filter((id) => id !== selectedChore.id),
      },
    }));
    setSelectedChoreId(null);
    setCompletionPersonId('');
    setNotice(`Completed ${selectedChore.name} for ${completionPerson.name}. +${coins} CC added.`);
  }

  function addReward(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedName = rewardName.trim();
    if (!trimmedName || rewardCost < 1) {
      return;
    }

    setStore((currentStore) => ({
      ...currentStore,
      rewards: [
        { id: createId('reward'), name: trimmedName, cost: Math.max(1, Math.round(rewardCost)) },
        ...currentStore.rewards,
      ],
    }));
    setRewardName('');
    setRewardCost(50);
  }

  function buyReward(reward: Reward) {
    const buyer = activePerson ?? store.people[0];
    if (!buyer) {
      return;
    }

    if (buyer.coins < reward.cost) {
      setNotice(`${buyer.name} needs ${reward.cost - buyer.coins} more CC for ${reward.name}.`);
      return;
    }

    setStore((currentStore) => ({
      ...currentStore,
      people: currentStore.people.map((person) =>
        person.id === buyer.id ? { ...person, coins: person.coins - reward.cost } : person
      ),
    }));
    setNotice(`${buyer.name} bought: ${reward.name}. Enjoy it.`);
  }

  function deleteReward(rewardId: string) {
    setStore((currentStore) => ({
      ...currentStore,
      rewards: currentStore.rewards.filter((reward) => reward.id !== rewardId),
    }));
  }

  return (
    <section className={`wheel-chores-page is-${experienceMode}-app`} aria-label="Intelligent Wheel of Chores">
      {showModeSwitch ? (
        <div className="wheel-chores-view-switch" aria-label="Choose Wheel of Chores rendering mode">
          <span>Render as</span>
          <div role="group" aria-label="Experience mode">
            <button
              type="button"
              className={experienceMode === 'web' ? 'is-active' : ''}
              aria-pressed={experienceMode === 'web'}
              onClick={() => setExperienceMode('web')}
            >
              Web App
            </button>
            <button
              type="button"
              className={experienceMode === 'phone' ? 'is-active' : ''}
              aria-pressed={experienceMode === 'phone'}
              onClick={() => setExperienceMode('phone')}
            >
              Phone App
            </button>
          </div>
        </div>
      ) : null}

      <div className={experienceMode === 'phone' ? 'wheel-chores-phone-shell' : 'wheel-chores-web-shell'}>
        {experienceMode === 'phone' ? (
          <div className="wheel-chores-phone-status" aria-hidden="true">
            <span>9:41</span>
            <span>CC</span>
          </div>
        ) : null}

        <header className="wheel-chores-header">
        <div>
          <p className="wheel-chores-eyebrow">Time-budgeted home ops</p>
          <h1>Intelligent Wheel of Chores</h1>
        </div>
        <div className="wheel-chores-balance" aria-label="Chore Coin Balance">
          <span>{activePerson?.coins ?? householdCoins}</span>
          <small>{activePerson ? `${activePerson.name} CC` : 'Household CC'}</small>
        </div>
        </header>

        <section className="wheel-chores-person-switcher" aria-label="Choose chore list">
          <button
            type="button"
            className={activePersonId === 'all' ? 'is-active' : ''}
            aria-pressed={activePersonId === 'all'}
            onClick={() => setActivePersonId('all')}
          >
            Everyone
          </button>
          {store.people.map((person) => (
            <button
              key={person.id}
              type="button"
              className={activePersonId === person.id ? 'is-active' : ''}
              aria-pressed={activePersonId === person.id}
              onClick={() => setActivePersonId(person.id)}
            >
              {person.name}
            </button>
          ))}
        </section>

        <nav className="wheel-chores-tabs" aria-label="Wheel of Chores tabs">
        <button type="button" className={activeTab === 'wheel' ? 'is-active' : ''} onClick={() => setActiveTab('wheel')}>
          The Wheel
        </button>
        <button type="button" className={activeTab === 'chores' ? 'is-active' : ''} onClick={() => setActiveTab('chores')}>
          Chore Manager
        </button>
        <button type="button" className={activeTab === 'people' ? 'is-active' : ''} onClick={() => setActiveTab('people')}>
          People
        </button>
        <button type="button" className={activeTab === 'rewards' ? 'is-active' : ''} onClick={() => setActiveTab('rewards')}>
          Reward Shop
        </button>
        </nav>

        {activeTab === 'wheel' ? (
        <div className="wheel-chores-dashboard">
          <section className="wheel-chores-panel wheel-chores-wheel-panel">
            <div className="wheel-chores-pointer" aria-hidden="true" />
            <div
              className="wheel-chores-wheel"
              style={{ background: wheelGradient, transform: `rotate(${rotation}deg)` }}
              aria-label="Today's chore wheel"
            >
              <div className="wheel-chores-wheel-center">
                <span>{todayChores.length}</span>
                <small>tasks</small>
              </div>
            </div>
            <button type="button" className="wheel-chores-spin" onClick={spinWheel} disabled={isSpinning || todayChores.length === 0}>
              SPIN
            </button>
          </section>

          <section className="wheel-chores-panel wheel-chores-controls">
            <div className="wheel-chores-budget-row">
              <label htmlFor="max-daily-minutes">Maximum Daily Chore Time</label>
              <input
                id="max-daily-minutes"
                type="number"
                min="5"
                max="240"
                value={store.maxDailyMinutes}
                onChange={(event) =>
                  setStore((currentStore) => ({
                    ...currentStore,
                    maxDailyMinutes: Math.max(5, Number(event.target.value)),
                  }))
                }
              />
            </div>
            <button type="button" className="wheel-chores-primary" onClick={generateWheel}>
              Generate Today's Wheel
            </button>
            <p className="wheel-chores-notice">{notice}</p>
            <div className="wheel-chores-stats">
              <span>{todayMinutes} / {store.maxDailyMinutes} min</span>
              <span>{availableChores.length} available</span>
              <span>{store.history.length} completed</span>
            </div>
            <ul className="wheel-chores-today-list">
              {todayChores.length === 0 ? (
                <li className="wheel-chores-empty">Generate a wheel to fill today&apos;s chore budget.</li>
              ) : (
                todayChores.map((chore) => (
                  <li key={chore.id}>
                    <span>{chore.name}</span>
                    <small>{chore.frequency} · {chore.minutes} min · {getCoinValue(chore)} CC · {getAssigneeNames(chore, store.people)}</small>
                  </li>
                ))
              )}
            </ul>
          </section>
        </div>
        ) : null}

        {activeTab === 'chores' ? (
        <div className="wheel-chores-manager">
          <form className="wheel-chores-panel wheel-chores-form" onSubmit={addChore}>
            <h2>Add chore</h2>
            <label>
              Name
              <input value={choreName} onChange={(event) => setChoreName(event.target.value)} placeholder="Vacuum hallway" />
            </label>
            <label>
              Frequency
              <select value={choreFrequency} onChange={(event) => setChoreFrequency(event.target.value as Frequency)}>
                {frequencies.map((frequency) => (
                  <option key={frequency} value={frequency}>{frequency}</option>
                ))}
              </select>
            </label>
            <label>
              Estimated Time
              <input type="number" min="1" value={choreMinutes} onChange={(event) => setChoreMinutes(Number(event.target.value))} />
            </label>
            <fieldset className="wheel-chores-assignees">
              <legend>Assigned to</legend>
              {store.people.map((person) => (
                <label key={person.id}>
                  <input
                    type="checkbox"
                    checked={choreAssigneeIds.includes(person.id)}
                    onChange={() => toggleChoreAssignee(person.id)}
                  />
                  {person.name}
                </label>
              ))}
            </fieldset>
            <button type="submit" className="wheel-chores-primary">Add Chore</button>
          </form>

          <section className="wheel-chores-panel">
            <h2>Frequency pools</h2>
            <div className="wheel-chores-pools">
              {pools.map((pool) => (
                <article key={pool.frequency}>
                  <strong>{pool.frequency}</strong>
                  <span>{pool.available} available</span>
                  <small>{pool.total} total</small>
                </article>
              ))}
            </div>
            <ul className="wheel-chores-list">
              {store.chores.map((chore) => (
                <li key={chore.id}>
                  <div>
                    <span>{chore.name}</span>
                    <small>{chore.frequency} · {chore.minutes} min · {getCoinValue(chore)} CC · {getAssigneeNames(chore, store.people)}</small>
                  </div>
                  <button type="button" onClick={() => deleteChore(chore.id)}>Delete</button>
                </li>
              ))}
            </ul>
          </section>
        </div>
        ) : null}

        {activeTab === 'people' ? (
        <div className="wheel-chores-manager">
          <form className="wheel-chores-panel wheel-chores-form" onSubmit={addPerson}>
            <h2>Add person</h2>
            <label>
              Name
              <input value={personName} onChange={(event) => setPersonName(event.target.value)} placeholder="Roommate name" />
            </label>
            <button type="submit" className="wheel-chores-primary">Add Person</button>
          </form>

          <section className="wheel-chores-panel">
            <h2>Household roster</h2>
            <ul className="wheel-chores-list">
              {store.people.map((person) => {
                const assignedCount = store.chores.filter((chore) => chore.assigneeIds.includes(person.id)).length;

                return (
                  <li key={person.id}>
                    <div>
                      <span>{person.name}</span>
                      <small>{person.coins} CC · {assignedCount} assigned chores</small>
                    </div>
                    <button type="button" onClick={() => deletePerson(person.id)} disabled={store.people.length <= 1}>Delete</button>
                  </li>
                );
              })}
            </ul>
          </section>
        </div>
        ) : null}

        {activeTab === 'rewards' ? (
        <div className="wheel-chores-manager">
          <form className="wheel-chores-panel wheel-chores-form" onSubmit={addReward}>
            <h2>Add reward</h2>
            <label>
              Reward
              <input value={rewardName} onChange={(event) => setRewardName(event.target.value)} placeholder="Movie night" />
            </label>
            <label>
              CC Cost
              <input type="number" min="1" value={rewardCost} onChange={(event) => setRewardCost(Number(event.target.value))} />
            </label>
            <button type="submit" className="wheel-chores-primary">Add Reward</button>
          </form>

          <section className="wheel-chores-panel">
            <h2>Reward shop</h2>
            <p className="wheel-chores-shop-note">
              Buying as {activePerson?.name ?? store.people[0]?.name ?? 'household'}.
            </p>
            <ul className="wheel-chores-list wheel-chores-reward-list">
              {store.rewards.map((reward) => (
                <li key={reward.id}>
                  <div>
                    <span>{reward.name}</span>
                    <small>{reward.cost} CC</small>
                  </div>
                  <div className="wheel-chores-reward-actions">
                    <button type="button" onClick={() => buyReward(reward)} disabled={(activePerson ?? store.people[0])?.coins < reward.cost}>Buy</button>
                    <button type="button" onClick={() => deleteReward(reward.id)}>Delete</button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>
        ) : null}
      </div>

      {selectedChore ? (
        <div className="wheel-chores-modal-backdrop" role="presentation">
          <section className="wheel-chores-modal" role="dialog" aria-modal="true" aria-labelledby="selected-chore-title">
            <p className="wheel-chores-eyebrow">Your spin landed on</p>
            <h2 id="selected-chore-title">{selectedChore.name}</h2>
            <p>{selectedChore.frequency} chore · {selectedChore.minutes} minutes · earns {getCoinValue(selectedChore)} CC · assigned to {getAssigneeNames(selectedChore, store.people)}</p>
            {completionPeople.length > 1 ? (
              <label className="wheel-chores-completion-person">
                Completed by
                <select value={completionPersonId} onChange={(event) => setCompletionPersonId(event.target.value)}>
                  {completionPeople.map((person) => (
                    <option key={person.id} value={person.id}>{person.name}</option>
                  ))}
                </select>
              </label>
            ) : null}
            <div className="wheel-chores-modal-actions">
              <button type="button" className="wheel-chores-primary" onClick={completeSelectedChore}>Complete Task</button>
              <button type="button" onClick={() => setSelectedChoreId(null)}>Close</button>
            </div>
          </section>
        </div>
      ) : null}
    </section>
  );
}

export default WheelOfChores;
